import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CloudUpload, ImagePlus, LogOut, RefreshCw } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "notistack";
import { connect } from "react-redux";
import { userDetails } from "../../models/userModels";
import { AddEmployeeImagesAction } from "../../stores/actions/employeeAction";
import { compressProfileImage } from "../../utility/imageCompression";
import { updateUserFaceImageAction } from "../../stores/actions/userAction";
import { GetEmployeeImagesService } from "../../services/employeeService";
import {
  getFaceImageValidationError,
  shouldRequireFaceRegistration,
  validateCompressedProfileImage,
} from "./utils";

export { shouldRequireFaceRegistration } from "./utils";

const FaceImageRegistrationModal = ({
  open,
  user,
  AddEmployeeImagesAction,
  updateUserFaceImageAction,
  onUploadComplete,
  onLogout,
}: FaceImageRegistrationModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | "">("");
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const checkRegistrationStatus = useCallback(
    async (isManual = false) => {
      const empId = user?.employeeId || user?.userid?.toString();
      if (!empId) {
        return;
      }

      if (isManual) {
        setCheckingStatus(true);
      }

      try {
        const response = await GetEmployeeImagesService({ employeeId: empId });
        const images = response?.data;
        if (Array.isArray(images) && images.length > 0) {
          const registeredUrl =
            images[0]?.singed_url || images[0]?.doc_name || "registered";
          updateUserFaceImageAction?.(registeredUrl);
          enqueueSnackbar("Face image verified successfully. Welcome!", {
            variant: "success",
          });
          return true;
        } else if (isManual) {
          enqueueSnackbar(
            "No registered face image found yet.",
            {
              variant: "info",
            },
          );
        }
      } catch (error: any) {
        if (isManual) {
          enqueueSnackbar(
            error?.message ||
            "Unable to verify face image status. Please try again.",
            {
              variant: "error",
            },
          );
        }
      } finally {
        if (isManual) {
          setCheckingStatus(false);
        }
      }
      return false;
    },
    [user?.employeeId, user?.userid, updateUserFaceImageAction, enqueueSnackbar],
  );

  // Auto-check on modal open
  useEffect(() => {
    if (open) {
      checkRegistrationStatus(false);
    }
  }, [open, checkRegistrationStatus]);

  // Auto-check when window / tab regains focus (e.g. user returns from another app/tab)
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleWindowFocus = () => {
      checkRegistrationStatus(false);
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [open, checkRegistrationStatus]);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setPreviewUrl((current) => {
        if (current?.startsWith("blob:")) {
          URL.revokeObjectURL(current);
        }
        return "";
      });
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileSelection = useCallback(
    async (file: File) => {
      const validationError = getFaceImageValidationError(file);

      if (validationError) {
        enqueueSnackbar(validationError, {
          variant: "error",
        });
        return;
      }

      let finalFile = file;
      if (file.size > 1 * 1024 * 1024) {
        setCompressing(true);
        enqueueSnackbar("Image exceeds 1 MB. Compressing image size...", {
          variant: "info",
        });
        try {
          finalFile = await compressProfileImage(file);
        } finally {
          setCompressing(false);
        }
      }

      const compressedError = validateCompressedProfileImage(finalFile);
      if (compressedError) {
        enqueueSnackbar(compressedError, {
          variant: "error",
        });
        return;
      }

      setSelectedFile(finalFile);
      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(URL.createObjectURL(finalFile));
    },
    [enqueueSnackbar, previewUrl],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: any[]) => {
      if (acceptedFiles.length > 0) {
        handleFileSelection(acceptedFiles[0]);
      } else if (fileRejections.length > 0) {
        const error = fileRejections[0].errors[0];
        if (error.code === "file-too-large") {
          enqueueSnackbar("Image size must be less than 20 MB.", {
            variant: "error",
          });
        } else if (error.code === "file-invalid-type") {
          enqueueSnackbar("Only PNG, JPG, and JPEG images are allowed.", {
            variant: "error",
          });
        } else {
          enqueueSnackbar(error.message || "Invalid file selection.", {
            variant: "error",
          });
        }
      }
    },
    [handleFileSelection, enqueueSnackbar],
  );

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
    isDragActive,
  } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
    multiple: false,
    onDrop,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar("Please choose an image before uploading.", {
        variant: "error",
      });
      return;
    }

    let fileToUpload = selectedFile;
    if (fileToUpload.size > 1 * 1024 * 1024) {
      setCompressing(true);
      fileToUpload = await compressProfileImage(fileToUpload);
      setCompressing(false);
    }

    const validationError = validateCompressedProfileImage(fileToUpload);
    if (validationError) {
      enqueueSnackbar(validationError, {
        variant: "error",
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append(
        "employeeId",
        user?.employeeId || user?.userid?.toString() || "",
      );
      formData.append("file", fileToUpload);

      await AddEmployeeImagesAction(formData);
      enqueueSnackbar("Face image uploaded successfully.", {
        variant: "success",
      });

      await new Promise((resolve) => window.setTimeout(resolve, 1000));
      onUploadComplete?.();
    } catch (error: any) {
      const backendError =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data === "string" ? error.response.data : null) ||
        (error?.message && !error.message.startsWith("Request failed with status code") ? error.message : null) ||
        "Unable to upload the face image. Please try again.";
      enqueueSnackbar(backendError, {
        variant: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const previewLabel = useMemo(() => {
    if (!selectedFile) {
      return "No image selected yet";
    }

    return `${selectedFile.name} (${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)`;
  }, [selectedFile]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={() => undefined}
      aria-labelledby="face-registration-modal-title"
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "640px" },
          maxWidth: "calc(100vw - 24px)",
          maxHeight: "none",
          overflow: "visible",
          borderRadius: { xs: 0, sm: 1.5 },
          border: "1px solid rgba(203, 213, 225, 0.95)",
          boxShadow: "0 8px 24px rgba(2, 12, 27, 0.16)",
          backgroundColor: "#FFFFFF",
          m: { xs: 0, sm: 1.5 },
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(2, 8, 20, 0.84)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Typography
                id="face-registration-modal-title"
                variant="h6"
                fontWeight={700}
                color="#002045"
              >
                Face Registration Required
              </Typography>
              <Typography variant="body2" color="#475569" sx={{ mt: 0.5 }}>
                Upload a clear face image to continue with biometric attendance.
              </Typography>
            </Box>
            {onLogout && (
              <Button
                variant="text"
                color="error"
                size="small"
                startIcon={<LogOut size={16} />}
                onClick={onLogout}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  flexShrink: 0,
                  color: "#DC2626",
                  "&:hover": {
                    bgcolor: "rgba(220, 38, 38, 0.08)",
                  },
                }}
              >
                Log Out
              </Button>
            )}
          </Box>

          <Box
            {...getRootProps()}
            sx={{
              width: "100%",
              minHeight: 154,
              border: isDragActive
                ? "1.5px dashed #002045"
                : "1.5px dashed #CBD5E1",
              borderRadius: 1.5,
              bgcolor: isDragActive ? "#F8FAFC" : "#F8F9FF",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              cursor: "pointer",
              textAlign: "center",
              p: 2,
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload size={24} color="#904D00" />
            <Typography variant="subtitle2" fontWeight={600} color="#002045">
              {isDragActive
                ? "Drop your image here"
                : "Choose or drag an image"}
            </Typography>
            <Typography variant="caption" color="#64748B">
              PNG, JPG, JPEG · Less than 1 MB
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              border: "1px solid #E2E8F0",
              bgcolor: "#F8FAFC",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={600} color="#002045">
                {selectedFile ? "Selected image" : "No image selected"}
              </Typography>
              <Typography
                variant="caption"
                color="#64748B"
                sx={{ display: "block", mt: 0.25 }}
              >
                {previewLabel}
              </Typography>
            </Box>

            <Box
              sx={{
                width: 92,
                height: 92,
                borderRadius: 1.25,
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                bgcolor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Face preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <ImagePlus size={28} color="#64748B" />
              )}
            </Box>
          </Box>

          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={
                checkingStatus ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <RefreshCw size={15} />
                )
              }
              onClick={() => checkRegistrationStatus(true)}
              disabled={checkingStatus || uploading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                justifyContent: { xs: "center", sm: "flex-start" },
                px: 1,
              }}
            >
              {checkingStatus
                ? "Checking status..."
                : "Uploaded on mobile? Check Status"}
            </Button>

            <Stack
              direction="row"
              spacing={1.2}
              justifyContent={{ xs: "stretch", sm: "flex-end" }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={openFilePicker}
                disabled={uploading}
                sx={{ flex: { xs: 1, sm: "initial" } }}
              >
                Choose Image
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                sx={{ flex: { xs: 1, sm: "initial" } }}
              >
                {uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Upload & Continue"
                )}
              </Button>
            </Stack>
          </Stack>

          <Typography variant="caption" color="#64748B">
            Image should be recent and not older than 6 months. If you already
            uploaded via the mobile app, click &quot;Check Status&quot; to continue.
          </Typography>
        </Stack>
      </Box>
    </Dialog>
  );
};

interface FaceImageRegistrationModalProps {
  open: boolean;
  user?: userDetails;
  AddEmployeeImagesAction?: any;
  updateUserFaceImageAction?: (imageUrl: string) => any;
  onUploadComplete?: () => void;
  onLogout?: () => void;
}

const mapDispatchToProps = {
  AddEmployeeImagesAction,
  updateUserFaceImageAction,
};

export default connect(null, mapDispatchToProps)(FaceImageRegistrationModal);
